import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { FileAccessResponse } from './file.service';
import { EMPTY, Observable, tap } from 'rxjs';
import { Course } from '../components/course/course';
import { CourseList, CourseQueryFilter } from '../components/course/course-list';
import { ParticipantCourseQueryFilter } from '../components/course/participant-course-list';
import { ebook } from '../components/course/ebook';
import { param } from 'jquery';

@Injectable({
  providedIn: 'root'
})
export class CourseService {

  apiUrl = environment.apiUrl

  httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(private http: HttpClient, private _authservice: AuthService) { }

  getCourseQueryParams(filter: any): HttpParams {
    const cleaned: Record<string, string | number> = {};
    Object.entries(filter || {}).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      cleaned[key] = value as string | number;
    });
    return new HttpParams({ fromObject: cleaned });
  }

  private toCourseApiPayload(course: Course, includeId = false) {
    const courseType = Number(course.courseType);
    const courseTypeId = courseType > 0 ? courseType : null;
    const payload: Record<string, unknown> = {
      courseName: course.courseName,
      courseShortDescription: course.courseShortDescription,
      courseDescription: course.courseDescription,
      materialId: course.materialId,
      languageId: course.languageId,
      tagId: course.tagId,
      courseThumbnailFileId: course.courseThumbnailFileId,
      courseThumbnailName: course.courseThumbnailName,
      courseStatus: course.courseStatus,
      coursePrograms: course.coursePrograms,
      isReplaced: course.isReplaced,
      courseType: courseTypeId,
    };

    if (includeId && course.id) {
      payload['id'] = course.id;
    }
    if (course.replacementCourseId) {
      payload['replacementCourseId'] = course.replacementCourseId;
    }

    return payload;
  }

  getCourseLanguages(): Observable<any[]> {
    const token = this._authservice.getAuthToken();
		const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    return this.http.get<any[]>(this.apiUrl + 'language/getApplicationLanguages', {headers:headers})
  }

  getCourseStatus(courseid?: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    if (courseid) {
      params = params.set('courseid', courseid);
    }
    return this.http.get<any[]>(this.apiUrl + 'course/getCourseStatus', { headers, params });
  }

  hasEnrolledUsers(id: string): Observable<any> {
    const token = this._authservice.getAuthToken();
		const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append('id', id.toString());
    return this.http.get<any>(this.apiUrl + "course/hasEnrolledUsers", {headers: headers, params: params});
  }

  createCourse(course: Course): Observable<any>
  {
    const token = this._authservice.getAuthToken();
		const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    return this.http.post<any>(this.apiUrl + 'course/createCourse', this.toCourseApiPayload(course, false), {headers: headers})
  }

  getAllCourses(query: CourseQueryFilter): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    const params = this.getCourseQueryParams(query);
    return this.http.get<CourseList[]>(this.apiUrl + 'course/getAllCourses', {params: params, headers: headers, observe: 'response'})

  }

  getCoursesForMaterials(): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    return this.http.get<any[]>(this.apiUrl + "course/getCoursesForMaterials", {headers: headers})
  }

  getCourseDetailsById(id: string): Observable<any>{
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append('id', id.toString());
    return this.http.get<any>(this.apiUrl + "course/getCourseDetailsById", {headers: headers, params: params})
  }

  deleteCourse(id: string): Observable<any>{
    const token = this._authservice.getAuthToken();
    const header = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams()
    params = params.append('id', id.toString());
    return this.http.post<any>(this.apiUrl + 'course/deleteCourse', null, {headers: header, params: params})
  }

  getCourseDetailsForUpdate(id: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append('id', id.toString());
    return this.http.get<any>(this.apiUrl + "course/getCourseDetailsForUpdate", {headers: headers, params: params})
  }

  getReplacementCourse(): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    return this.http.get<any[]>(this.apiUrl + "course/getReplacementCourses", {headers: headers})

  }

  updateCourse(course: Course): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    return this.http.post<any>(this.apiUrl + "course/updateCourse", this.toCourseApiPayload(course, true), {headers: headers})
  }

  getCourseAuditLogs(id: string): Observable<any>{
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append('id', id.toString());
    return this.http.get<any>(this.apiUrl + "course/getCourseAuditLogs", {headers, params});
  }

  getSpecialPermissionCourses(query: CourseQueryFilter, userid: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams()
    params = this.getCourseQueryParams(query);
    params = params.append('participantUserId',userid)
    return this.http.get<CourseList[]>(this.apiUrl + 'course/getSpecialPermissionCourses', {params: params, headers: headers, observe: 'response'})
  }

  getRecommendedCourses(query: CourseQueryFilter, userid: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = this.getCourseQueryParams(query);
    params = params.append('userId', userid)
    return this.http.get<CourseList[]>(this.apiUrl + 'course/getRecommendedCourses', {params: params, headers: headers, observe: 'response'})
  }

  getParticipantCourseStatus(): Observable<any>
  {
    const token = this._authservice.getAuthToken();
		const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    return this.http.get<any[]>(this.apiUrl + 'course/getParticipantCourseStatus', {headers: headers})
  }


  getParticipantCourses(query: ParticipantCourseQueryFilter, userid: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = this.getCourseQueryParams(query);
    params = params.append('userId', userid)
    return this.http.get<CourseList[]>(this.apiUrl + 'course/getParticipantCourses', {params: params, headers: headers, observe: 'response'})
  }

  toggleFavouriteCourse(courseid: string, userid: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append('courseId', courseid);
    params = params.append('userId', userid)
    return this.http.put<any>(this.apiUrl + 'course/toggleFavouriteCourse', null, {params: params, headers: headers, observe: 'response'})
  }

  getCourseDetailsForParticipant(courseId: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append("courseId", courseId);
    return this.http.get<any[]>(this.apiUrl + 'course/getCourseDetailsForParticipant', {headers: headers, params: params})
  }

  participantStartCourse(courseId: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append("courseId", courseId);
    return this.http.post<any>(this.apiUrl + 'course/participantStartCourse', {} , {headers: headers, params: params})
  }

  participantCompleteCourse(courseId: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append("courseId", courseId);
    return this.http.post<any>(this.apiUrl + 'course/participantCompleteCourse', {}, {headers: headers, params: params})
  }

  participantReOpenCourse(courseId: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append("courseId", courseId);
    return this.http.post<any>(this.apiUrl + 'course/participantReOpenCourse', {}, {headers: headers, params: params})
  }

  getCourseEbookFile(courseId: string): Observable<FileAccessResponse>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append("courseId", courseId);
    return this.http.get<FileAccessResponse>(this.apiUrl + 'course/getCourseEbookFile', {headers: headers, params: params});
  }

  getCourseMaterialFile(courseId: string, materialId: string): Observable<FileAccessResponse>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append("courseId", courseId);
    params = params.append("materialId", materialId);
    return this.http.get<FileAccessResponse>(this.apiUrl + 'course/getCourseMaterialFile', {headers: headers, params: params});
  }

  private saveTimeout: ReturnType<typeof setTimeout> | undefined;

  /**
   * Saves PDF form data immediately (no debounce). Use before navigation or other
   * actions that must persist the current material's answers without waiting 2s.
   */
  savePdfFormDataImmediate(ebookData: ebook): Observable<any> {
    clearTimeout(this.saveTimeout);
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ Authorization: 'Bearer ' + token });
    return this.http.post<any>(this.apiUrl + 'course/savePdfData', ebookData, { headers });
  }

  /** Debounced save while the user edits (avoids posting on every keystroke). */
  submitPdfFormData(ebookData: ebook): Observable<any> {
    clearTimeout(this.saveTimeout);

    this.saveTimeout = setTimeout(() => {
      this.savePdfFormDataImmediate(ebookData).subscribe({
        next: (response) => console.log('Saved successfully:', response),
        error: (error) => console.error('Error while saving:', error),
      });
    }, 2000);

    return EMPTY;
  }

  getCourseEbookData(courseId: string, materialId: string, userId: string): Observable<any> {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    let params = new HttpParams();
    params = params.append("courseId", courseId);
    params = params.append("materialId", materialId);
    params = params.append("userId", userId);
    return this.http.get<any[]>(this.apiUrl + 'course/getCourseEbookData', {headers: headers, params: params});
  }

  getRecomendedCoursesForParticipantDashboard(query: ParticipantCourseQueryFilter): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    const params = this.getCourseQueryParams(query);
    return this.http.get<CourseList[]>(this.apiUrl + 'course/getRecommendedCoursesForParticipantDashboard', {headers: headers, params: params, observe: 'response'})
  }

  getStartingCourseForParticipant(): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    return this.http.get<any>(this.apiUrl + 'course/getStartingCourseForParticipant', {headers: headers})
  }

  changeCourseStatusFromPublishedToDraft(courseid: string): Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({'Authorization': 'Bearer ' + token});
    let params = new HttpParams();
    params = params.append('id',courseid)
    return this.http.get(this.apiUrl + 'course/changeCourseStatusFromPublishedToDraft', {headers:headers,params:params})
  }

  getCourseTypes():Observable<any>
  {
    const token = this._authservice.getAuthToken();
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + token });
    return this.http.get(this.apiUrl + 'course/getCourseTypes', {headers: headers});
  }
}
